import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import ConfirmModal from '../../components/ConfirmModal';

type Member = { userId: string; name: string; email: string; role: number };
type UserRef = { id: string; name: string; email: string };
type GroupRef = { id: string; name: string; members: number };
type Detail = {
    id: string; name: string; description: string;
    isPublic: boolean; allowMessages: boolean; allowImages: boolean;
    enableMeeting: boolean; jitsiRoomName: string | null; meetUrl: string;
    members: Member[];
};

const ROLE_LABELS = ["Člen", "Moderátor"];

export default function AdminChannelDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = new ApiHelper();

    const [d, setD] = useState<Detail | null>(null);
    const [form, setForm] = useState({ name: "", description: "", isPublic: true, allowMessages: true, allowImages: true, enableMeeting: false });
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<UserRef[]>([]);
    const [groups, setGroups] = useState<GroupRef[]>([]);
    const [groupId, setGroupId] = useState("");
    const [confirmDel, setConfirmDel] = useState(false);
    const debounce = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => { load(); loadGroups(); }, [id]);
    useEffect(() => {
        clearTimeout(debounce.current);
        if (!search.trim()) { setResults([]); return; }
        debounce.current = setTimeout(searchUsers, 300);
        return () => clearTimeout(debounce.current);
    }, [search]);

    async function load() {
        try {
            const data: Detail = await (await api.adminChannelsApi.detail(id!)).json();
            setD(data);
            setForm({
                name: data.name, description: data.description, isPublic: data.isPublic,
                allowMessages: data.allowMessages, allowImages: data.allowImages, enableMeeting: data.enableMeeting,
            });
        } catch (x: any) { api.adminError(x); }
    }
    async function searchUsers() {
        try { setResults(await (await api.adminGroupsApi.searchUsers({ search: search.trim() })).json()); } catch (x: any) { api.adminError(x); }
    }
    async function loadGroups() {
        try { setGroups(await (await api.adminGroupsApi.list()).json()); } catch (x: any) { api.adminError(x); }
    }
    async function addGroup() {
        if (!groupId) return;
        const ok = await run(api.adminChannelsApi.addMembersFromGroup(id!, { groupId }), "Členové skupiny přidáni");
        if (ok) setGroupId("");
    }
    async function run(p: Promise<any>, ok: string): Promise<boolean> {
        try { await p; api.alert.success(ok); await load(); return true; } catch (x: any) { api.adminError(x); return false; }
    }

    async function saveSettings() {
        if (!form.name.trim()) { api.alert.error("Zadej název kanálu"); return; }
        await run(api.adminChannelsApi.update(id!, { ...form, name: form.name.trim() }), "Uloženo");
    }

    if (!d) return <div><span className="backLink" onClick={() => navigate("/admin/komunita")}>← Komunita</span></div>;

    return (
        <div>
            <span className="backLink" onClick={() => navigate("/admin/komunita")}>← Komunita</span>
            <div className="adminHead">
                <div><h1>{d.name}</h1><p>{d.isPublic ? "Veřejný kanál" : d.members.length + " členů"}</p></div>
                <button className="aBtn danger" onClick={() => setConfirmDel(true)}>Smazat kanál</button>
            </div>

            <div className="aCard">
                <h2>Nastavení kanálu</h2>
                <div className="aField"><label>Název</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div className="aField"><label>Popis</label><input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Krátký popis kanálu" /></div>

                <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    <label className="toggle"><input type="checkbox" checked={form.isPublic} onChange={e => setForm({ ...form, isPublic: e.target.checked })} /><i className="slider" /> Dostupný všem uživatelům (jinak jen přiřazeným členům)</label>
                    <label className="toggle"><input type="checkbox" checked={form.allowMessages} onChange={e => setForm({ ...form, allowMessages: e.target.checked })} /><i className="slider" /> Povolit psaní zpráv</label>
                    <label className="toggle"><input type="checkbox" checked={form.allowImages} onChange={e => setForm({ ...form, allowImages: e.target.checked })} /><i className="slider" /> Povolit sdílení obrázků</label>
                    <label className="toggle"><input type="checkbox" checked={form.enableMeeting} onChange={e => setForm({ ...form, enableMeeting: e.target.checked })} /><i className="slider" /> Povolit Jitsi schůzku</label>
                </div>

                {d.enableMeeting && d.meetUrl &&
                    <p className="empty" style={{ marginTop: "10px" }}>Odkaz na schůzku: <a href={d.meetUrl} target="_blank" rel="noopener noreferrer">{d.meetUrl}</a></p>}

                <div style={{ marginTop: "14px" }}><button className="aBtn" onClick={saveSettings}>Uložit nastavení</button></div>
            </div>

            {/* Membership only matters for private channels — public channels are open to everyone. */}
            {!form.isPublic &&
                <div className="aCard" style={{ marginTop: "18px" }}>
                    <h2>Členové ({d.members.length})</h2>
                    <div className="rowList">
                        {d.members.map(m => (
                            <div key={m.userId} className="lineItem">
                                <span className="liText">{m.name || "—"} · {m.email}</span>
                                <span className="acts">
                                    <select value={m.role} onChange={e => run(api.adminChannelsApi.setMemberRole(d.id, m.userId, { userId: m.userId, role: Number(e.target.value) }), "Role uložena")}>
                                        {ROLE_LABELS.map((r, i) => <option key={i} value={i}>{r}</option>)}
                                    </select>
                                    <span className="x" onClick={() => run(api.adminChannelsApi.removeMember(d.id, m.userId), "Odebrán")}>×</span>
                                </span>
                            </div>
                        ))}
                        {d.members.length === 0 && <span className="empty">Kanál nemá členy.</span>}
                    </div>
                    <div className="aField" style={{ marginTop: "12px" }}><label>Přidat člena</label><input placeholder="Hledat podle jména / e-mailu…" value={search} onChange={e => setSearch(e.target.value)} /></div>
                    {results.length > 0 &&
                        <div className="rowList">
                            {results.filter(r => !d.members.some(m => m.userId === r.id)).map(r => (
                                <div key={r.id} className="lineItem add" onClick={() => { run(api.adminChannelsApi.addMember(d.id, { userId: r.id, role: 0 }), "Přidán"); setSearch(""); setResults([]); }}>
                                    <span>{r.name || "—"} · {r.email}</span><span className="plus">+</span>
                                </div>
                            ))}
                        </div>
                    }
                    {groups.length > 0 &&
                        <div className="aField" style={{ marginTop: "16px" }}>
                            <label>Přidat celou skupinu</label>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <select value={groupId} onChange={e => setGroupId(e.target.value)} style={{ flex: 1 }}>
                                    <option value="">— vyber skupinu —</option>
                                    {groups.map(g => <option key={g.id} value={g.id}>{g.name} ({g.members})</option>)}
                                </select>
                                <button className="aBtn" style={{ flex: "none" }} disabled={!groupId} onClick={addGroup}>Přidat členy</button>
                            </div>
                        </div>
                    }
                </div>
            }

            <ConfirmModal open={confirmDel} title="Smazat kanál?" message={"Kanál „" + d.name + "\" i všechny jeho zprávy budou trvale smazány."} confirmLabel="Smazat" cancelLabel="Zrušit" onConfirm={() => { setConfirmDel(false); run(api.adminChannelsApi.remove(d.id), "Kanál smazán").then(ok => { if (ok) navigate("/admin/komunita"); }); }} onClose={() => setConfirmDel(false)} />
        </div>
    );
}
